import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Spinner } from "@/components/ui/Primitives";
import { api } from "@/lib/api";
import { Printer, FileBarChart2 } from "lucide-react";

const CATEGORIAS = ["cx1", "cx2", "cx3", "cx3g", "cx4"];
const CAT_LABEL = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };

function fmtNum(v, d = 0) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtDateBR(iso) {
  if (!iso) return "—";
  const d = new Date(iso.slice(0, 10) + "T12:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-BR");
}
function todayISO() { return new Date().toISOString().slice(0, 10); }
function firstOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function TerceirosRelatorioCliente() {
  const [tipo, setTipo] = useState("entradas"); // 'entradas' | 'saidas' | 'comparativo'
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState("");
  const [from, setFrom] = useState(firstOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);        // dado da aba single (entradas/saidas)
  const [entradas, setEntradas] = useState(null);
  const [saidas, setSaidas] = useState(null);
  const [estoque, setEstoque] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Carrega lista de clientes de terceiros
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reports/terceiros/top-clientes?limit=999");
        const nomes = (r.data?.items || [])
          .map((x) => x.cliente_dono)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "pt-BR"));
        setClientes(nomes);
      } catch { /* silêncio */ }
    })();
  }, []);

  // Recarrega ao trocar tipo, mantendo cliente e datas
  useEffect(() => { setData(null); setEntradas(null); setSaidas(null); setEstoque(null); setErr(""); }, [tipo]);

  const carregar = async () => {
    if (!cliente) return;
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ cliente_dono: cliente });
      if (from) params.set("from_", from);
      if (to) params.set("to", to);
      if (tipo === "comparativo") {
        const [rE, rS, rEst] = await Promise.all([
          api.get(`/reports/terceiros/cargas-cliente?${params.toString()}`),
          api.get(`/reports/terceiros/retiradas-cliente?${params.toString()}`),
          api.get(`/reports/terceiros/estoque-cliente?cliente_dono=${encodeURIComponent(cliente)}`),
        ]);
        setEntradas(rE.data);
        setSaidas(rS.data);
        setEstoque(rEst.data);
      } else {
        const path = tipo === "saidas"
          ? "/reports/terceiros/retiradas-cliente"
          : "/reports/terceiros/cargas-cliente";
        const r = await api.get(`${path}?${params.toString()}`);
        setData(r.data);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  const totais = data?.totais || {};

  return (
    <Layout
      title={tipo === "saidas" ? "Relatório de Retiradas — Cliente" : tipo === "comparativo" ? "Comparativo Entradas × Saídas — Cliente" : "Relatório de Beneficiamento — Cliente"}
      subtitle={tipo === "saidas"
        ? "Retiradas de um cliente com CX1..CX4 e sacos"
        : tipo === "comparativo"
        ? "Compare o que entrou (beneficiado) versus o que saiu (retirado) e o saldo em depósito"
        : "Cargas maquinadas de um cliente com CX1..CX4, sacos e quebra"}
      breadcrumb="Beneficiamento a Terceiros / Relatório por Cliente"
      actions={
        <div className="flex items-center gap-2 print:hidden">
          {(data || entradas || saidas) && (
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-500 hover:bg-brand-600 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
              data-testid="terceiros-report-print-btn"
            >
              <Printer size={14} /> Imprimir
            </button>
          )}
        </div>
      }
    >
      <div className="terceiros-report">
        <style>{`
          @media print {
            @page { size: A4 landscape; margin: 8mm 10mm; }
            html, body { font-size: 10pt; }
            .terceiros-report { color: #000; }
            .terceiros-report h1 { font-size: 16pt; margin-bottom: 4px; }
            .terceiros-report table { font-size: 9pt; border-collapse: collapse; width: 100%; }
            .terceiros-report thead { display: table-header-group; }
            .terceiros-report thead tr { background: #f3f3f3 !important; }
            .terceiros-report th, .terceiros-report td { padding: 2px 6px !important; line-height: 1.25 !important; }
            .terceiros-report tbody tr { page-break-inside: avoid; }
            .terceiros-report tbody tr:nth-child(even) { background: #fafafa !important; }
            .terceiros-report tfoot tr { background: #eaeaea !important; }
            .terceiros-report .kpi-print-row { display: grid !important; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-bottom: 6px !important; }
            .terceiros-report .kpi-print-row > div {
              border: 1px solid #ddd; padding: 4px 6px; border-radius: 4px;
              box-shadow: none !important;
            }
            .terceiros-report .kpi-print-row .kpi-val { font-size: 11pt; margin-top: 0 !important; }
            .terceiros-report .signatures { margin-top: 18mm !important; }
          }
        `}</style>

        {/* Toggle Entradas / Saídas */}
        <div className="mb-4 inline-flex rounded-lg bg-stone-100 p-1 print:hidden" data-testid="terceiros-relatorio-tipo-toggle">
          <button
            onClick={() => setTipo("entradas")}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${tipo === "entradas" ? "bg-white text-brand-600 shadow-sm" : "text-ink-500 hover:text-ink-900"}`}
            data-testid="tipo-entradas"
          >
            Entradas (beneficiadas)
          </button>
          <button
            onClick={() => setTipo("saidas")}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${tipo === "saidas" ? "bg-white text-brand-600 shadow-sm" : "text-ink-500 hover:text-ink-900"}`}
            data-testid="tipo-saidas"
          >
            Saídas (retiradas)
          </button>
          <button
            onClick={() => setTipo("comparativo")}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors ${tipo === "comparativo" ? "bg-white text-brand-600 shadow-sm" : "text-ink-500 hover:text-ink-900"}`}
            data-testid="tipo-comparativo"
          >
            Comparativo (E × S)
          </button>
        </div>

        {/* Filtros */}
        <Card className="p-5 mb-6 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_150px_auto] gap-3 items-end">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">
                Cliente
              </label>
              <input
                type="text"
                list="cliente-list"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                placeholder="Digite ou selecione o cliente"
                className="w-full h-10 rounded-md border border-ink-200 px-3 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                data-testid="terceiros-relatorio-cliente"
              />
              <datalist id="cliente-list">
                {clientes.map((f) => <option key={f} value={f} />)}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">De</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full h-10 rounded-md border border-ink-200 px-3 text-sm outline-none focus:ring-2 focus:ring-brand-500" data-testid="terceiros-relatorio-from" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Até</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-10 rounded-md border border-ink-200 px-3 text-sm outline-none focus:ring-2 focus:ring-brand-500" data-testid="terceiros-relatorio-to" />
            </div>
            <button
              onClick={carregar}
              disabled={!cliente || loading}
              className="h-10 px-5 rounded-md bg-brand-500 hover:bg-brand-600 disabled:bg-stone-300 text-white text-sm font-semibold uppercase tracking-wider transition-colors inline-flex items-center gap-1.5"
              data-testid="terceiros-relatorio-load"
            >
              {loading ? <Spinner size={16} /> : <><FileBarChart2 size={16} /> Carregar</>}
            </button>
          </div>
          {err && <div className="mt-3 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
        </Card>

        {data && tipo !== "comparativo" && (
          <>
            <div className="hidden print:block mb-6">
              <h1 className="text-2xl font-bold">
                {tipo === "saidas"
                  ? "Relatório de Retiradas (Beneficiamento a Terceiros)"
                  : "Relatório de Beneficiamento a Terceiros"}
              </h1>
              <div className="text-sm mt-1"><b>Cliente:</b> {data.cliente_dono}</div>
              <div className="text-sm"><b>Período:</b> {from || "início"} até {to || "hoje"}</div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 kpi-print-row">
              <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">{tipo === "saidas" ? "Retiradas" : "Cargas"}</div><div className="font-mono text-xl font-semibold text-ink-900 mt-1 kpi-val" data-testid="terceiros-relatorio-total-cargas">{fmtNum(totais.n_retiradas ?? totais.n_cargas ?? 0)}</div></Card>
              <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Sacos totais</div><div className="font-mono text-xl font-semibold text-brand-600 mt-1 kpi-val" data-testid="terceiros-relatorio-total-sacos">{fmtNum(totais.sacos_total || 0)}</div></Card>
              {tipo === "saidas" ? (
                <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Pallets retirados</div><div className="font-mono text-xl font-semibold text-ink-900 mt-1 kpi-val">{fmtNum(totais.total_pallets || 0)}</div></Card>
              ) : (
                <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Peso líquido</div><div className="font-mono text-xl font-semibold text-ink-900 mt-1 kpi-val">{fmtNum(totais.peso_liquido || 0)} kg</div></Card>
              )}
              {tipo === "saidas" ? (
                <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Média por retirada</div><div className="font-mono text-xl font-semibold text-amber-700 mt-1 kpi-val">{fmtNum(totais.n_retiradas > 0 ? (totais.sacos_total || 0) / totais.n_retiradas : 0)} sc</div></Card>
              ) : (
                <Card className="p-4"><div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Quebra média</div><div className="font-mono text-xl font-semibold text-amber-700 mt-1 kpi-val">{fmtNum(totais.quebra_media_pct || 0, 2)}%</div></Card>
              )}
            </div>

            {/* Tabela */}
            <Card className="overflow-hidden">
              <table className="w-full text-sm" data-testid="terceiros-relatorio-table">
                <thead className="bg-stone-50 border-b border-tertiary">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">{tipo === "saidas" ? "Retirada" : "NF"}</th>
                    <th className="px-4 py-3">Motorista</th>
                    {tipo === "saidas" && <th className="px-4 py-3">Vendedor</th>}
                    {tipo === "entradas" && <th className="px-3 py-3 text-right">Peso Entrada</th>}
                    {CATEGORIAS.map((c) => (
                      <th key={c} className="px-3 py-3 text-right">{CAT_LABEL[c]}</th>
                    ))}
                    <th className="px-3 py-3 text-right">Total sacos</th>
                    {tipo === "saidas"
                      ? <th className="px-3 py-3 text-right">Pallets</th>
                      : <th className="px-3 py-3 text-right">Quebra %</th>}
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r) => (
                    <tr key={r.invoice_id || r.retirada_id} className="border-b border-tertiary last:border-0 hover:bg-stone-50" data-testid={`terceiros-relatorio-row-${r.nota_numero || r.numero || (r.invoice_id || r.retirada_id).slice(0, 8)}`}>
                      <td className="px-4 py-3 font-mono whitespace-nowrap">{fmtDateBR(r.data)}</td>
                      <td className="px-4 py-3 font-mono">{tipo === "saidas" ? (r.numero || "—") : `#${r.nota_numero || "—"}`}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-ink-900">{r.motorista}</div>
                        {r.placa && <div className="text-xs text-ink-500 font-mono">{r.placa}</div>}
                      </td>
                      {tipo === "saidas" && (
                        <td className="px-4 py-3 text-ink-800">{r.vendedor || "—"}</td>
                      )}
                      {tipo === "entradas" && (
                        <td className="px-3 py-3 font-mono text-right font-semibold text-ink-900">{fmtNum(r.peso_liquido || 0, 0)} kg</td>
                      )}
                      {CATEGORIAS.map((c) => (
                        <td key={c} className="px-3 py-3 font-mono text-right">{fmtNum(r[c] || 0)}</td>
                      ))}
                      <td className="px-3 py-3 font-mono text-right font-bold text-brand-700">{fmtNum(r.sacos_total || 0)}</td>
                      {tipo === "saidas"
                        ? <td className="px-3 py-3 font-mono text-right text-ink-500">{fmtNum(r.total_pallets || 0)}</td>
                        : <td className="px-3 py-3 font-mono text-right text-amber-700">{fmtNum(r.quebra_pct || 0, 2)}%</td>}
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr><td colSpan={3 + (tipo === "saidas" ? 1 : 0) + (tipo === "entradas" ? 1 : 0) + CATEGORIAS.length + 2} className="px-4 py-8 text-center text-ink-500">
                      {tipo === "saidas"
                        ? "Nenhuma retirada encontrada para este cliente no período."
                        : "Nenhuma carga beneficiada encontrada para este cliente no período."}
                    </td></tr>
                  )}
                </tbody>
                {data.rows.length > 0 && (
                  <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold">
                    <tr>
                      <td className="px-4 py-3" colSpan={tipo === "saidas" ? 4 : 3}>TOTAL</td>
                      {tipo === "entradas" && (
                        <td className="px-3 py-3 font-mono text-right">{fmtNum(totais.peso_liquido || 0, 0)} kg</td>
                      )}
                      {CATEGORIAS.map((c) => (
                        <td key={c} className="px-3 py-3 font-mono text-right">{fmtNum(totais[c] || 0)}</td>
                      ))}
                      <td className="px-3 py-3 font-mono text-right text-brand-700">{fmtNum(totais.sacos_total || 0)}</td>
                      {tipo === "saidas"
                        ? <td className="px-3 py-3 font-mono text-right text-ink-500">{fmtNum(totais.total_pallets || 0)}</td>
                        : <td className="px-3 py-3 font-mono text-right text-amber-700">{fmtNum(totais.quebra_media_pct || 0, 2)}%</td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </Card>

            {/* Assinaturas (só na impressão) */}
            <div className="hidden print:block mt-16 signatures">
              <div className="grid grid-cols-2 gap-16">
                <div className="text-center">
                  <div className="border-t border-black pt-2 text-xs">Cebolas Serenata</div>
                </div>
                <div className="text-center">
                  <div className="border-t border-black pt-2 text-xs">{data.cliente_dono}</div>
                </div>
              </div>
            </div>
          </>
        )}

        {tipo === "comparativo" && (entradas || saidas) && (
          <ComparativoView
            cliente={entradas?.cliente_dono || saidas?.cliente_dono || cliente}
            from={from} to={to}
            entradas={entradas} saidas={saidas} estoque={estoque}
          />
        )}
      </div>
    </Layout>
  );
}

function ComparativoView({ cliente, from, to, entradas, saidas, estoque }) {
  const tE = entradas?.totais || {};
  const tS = saidas?.totais || {};
  const CATS = ["cx1", "cx2", "cx3", "cx3g", "cx4"];
  const CAT_LABELS = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };
  const [showOrfaos, setShowOrfaos] = useState(false);

  // Cálculos por categoria
  const saldos = {};
  const estoqueReal = {};
  const divergencias = {};
  CATS.forEach((c) => {
    saldos[c] = (tE[c] || 0) - (tS[c] || 0);
    estoqueReal[c] = estoque ? (estoque[c] || 0) : null;
    divergencias[c] = estoque ? (estoqueReal[c] - saldos[c]) : 0;
  });
  const saldoTotal = (tE.sacos_total || 0) - (tS.sacos_total || 0);
  const estoqueTotal = estoque ? (estoque.sacos_total || 0) : null;
  const divergenciaTotal = estoque ? (estoqueTotal - saldoTotal) : 0;
  const temDivergencia = estoque && Math.abs(divergenciaTotal) > 0.5;
  const nOrfaos = estoque?.pallets_orfaos?.length || 0;

  return (
    <>
      {/* Cabeçalho impressão */}
      <div className="hidden print:block mb-4">
        <h1 className="text-2xl font-bold">Comparativo Entradas × Saídas — Beneficiamento a Terceiros</h1>
        <div className="text-sm mt-1"><b>Cliente:</b> {cliente}</div>
        <div className="text-sm"><b>Período:</b> {from || "início"} até {to || "hoje"}</div>
      </div>

      {/* Resumo comparativo */}
      <Card className="p-4 mb-4" data-testid="comparativo-resumo">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">
            Comparativo Entradas × Saídas × Estoque físico
          </div>
          {temDivergencia && (
            <button
              onClick={() => setShowOrfaos(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-danger hover:bg-red-700 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors print:hidden"
              data-testid="btn-investigar-divergencia"
            >
              🔍 Investigar {nOrfaos > 0 && `(${nOrfaos} órfão${nOrfaos > 1 ? "s" : ""})`}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-ink-200">
            <thead className="bg-stone-50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-ink-500">
                <th className="px-3 py-2"></th>
                {CATS.map((c) => (<th key={c} className="px-3 py-2 text-right">{CAT_LABELS[c]}</th>))}
                <th className="px-3 py-2 text-right">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-emerald-50/40 border-t border-ink-200">
                <td className="px-3 py-2 font-semibold text-emerald-800">Entradas</td>
                {CATS.map((c) => (<td key={c} className="px-3 py-2 text-right font-mono text-emerald-900">{fmtNum(tE[c] || 0)}</td>))}
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-900">{fmtNum(tE.sacos_total || 0)}</td>
              </tr>
              <tr className="bg-purple-50/40 border-t border-ink-200">
                <td className="px-3 py-2 font-semibold text-purple-800">Saídas</td>
                {CATS.map((c) => (<td key={c} className="px-3 py-2 text-right font-mono text-purple-900">{fmtNum(tS[c] || 0)}</td>))}
                <td className="px-3 py-2 text-right font-mono font-bold text-purple-900">{fmtNum(tS.sacos_total || 0)}</td>
              </tr>
              <tr className="bg-stone-50 border-t border-ink-200 text-[12px]">
                <td className="px-3 py-2 text-ink-600 italic">Saldo teórico (E − S)</td>
                {CATS.map((c) => (
                  <td key={c} className={`px-3 py-2 text-right font-mono italic ${saldos[c] < 0 ? "text-danger" : "text-ink-600"}`}>
                    {fmtNum(saldos[c])}
                  </td>
                ))}
                <td className={`px-3 py-2 text-right font-mono italic ${saldoTotal < 0 ? "text-danger" : "text-ink-600"}`}>{fmtNum(saldoTotal)}</td>
              </tr>
              {estoque && (
                <tr className="bg-brand-50 border-t-2 border-ink-900">
                  <td className="px-3 py-2 font-bold text-brand-800">Estoque físico atual</td>
                  {CATS.map((c) => (
                    <td key={c} className="px-3 py-2 text-right font-mono font-bold text-brand-800">
                      {fmtNum(estoqueReal[c] || 0)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-mono font-bold text-brand-800">{fmtNum(estoqueTotal || 0)}</td>
                </tr>
              )}
              {temDivergencia && (
                <tr className="bg-red-50 border-t border-red-300">
                  <td className="px-3 py-2 font-semibold text-danger">Divergência (Estoque − Teórico)</td>
                  {CATS.map((c) => (
                    <td key={c} className={`px-3 py-2 text-right font-mono font-semibold ${Math.abs(divergencias[c]) > 0.5 ? "text-danger" : "text-ink-400"}`}>
                      {divergencias[c] > 0 ? "+" : ""}{fmtNum(divergencias[c])}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-mono font-bold text-danger">
                    {divergenciaTotal > 0 ? "+" : ""}{fmtNum(divergenciaTotal)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-ink-500 mt-2">
          <strong>Estoque físico</strong> = pallets ainda no depósito (fonte da verdade).{" "}
          {temDivergencia
            ? "⚠️ Divergência detectada — clique em Investigar para ver o que causou."
            : estoque
            ? "✓ Estoque físico bate com o saldo teórico."
            : "Carregando estoque..."}
        </div>
      </Card>

      {/* Modal investigar */}
      {showOrfaos && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print:hidden" onClick={() => setShowOrfaos(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 sticky top-0 bg-white">
              <div>
                <h3 className="font-heading text-lg font-bold text-ink-900">Investigação de divergência</h3>
                <div className="text-xs text-ink-500">Cliente: {cliente} · Diferença: {divergenciaTotal > 0 ? "+" : ""}{fmtNum(divergenciaTotal)} sc</div>
              </div>
              <button onClick={() => setShowOrfaos(false)} className="text-ink-400 hover:text-ink-700 text-2xl leading-none" data-testid="close-investigar">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <strong>Como interpretar:</strong> O sistema encontrou {nOrfaos} pallet(s) órfão(s) — pallets em estoque físico que apontam para NFs que <b>não existem mais</b> no cadastro. Isso acontece quando uma NF é excluída DEPOIS que os pallets já foram gerados, ou quando um beneficiamento falha e é refeito. Esses pallets contribuem para o estoque físico mas não aparecem como "Entradas" no relatório.
              </div>
              {nOrfaos === 0 ? (
                <div className="text-sm text-ink-500 py-4 text-center">
                  Nenhum pallet órfão encontrado. A divergência pode ter outra causa (ex: retirada não lançada). Entre em contato com suporte.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-ink-200 rounded-md">
                    <thead className="bg-stone-50 border-b border-ink-200 text-[11px] uppercase tracking-wider text-ink-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Código</th>
                        <th className="px-3 py-2 text-left">Categoria</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-right">Expedido</th>
                        <th className="px-3 py-2 text-right">Restante</th>
                        <th className="px-3 py-2 text-left">Criado em</th>
                        <th className="px-3 py-2 text-left">NF ausente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estoque.pallets_orfaos.map((pl) => (
                        <tr key={pl.pallet_id} className="border-b border-ink-100">
                          <td className="px-3 py-1.5 font-mono text-[12px]">{pl.codigo || pl.pallet_id.slice(0, 8)}</td>
                          <td className="px-3 py-1.5 text-[12px] uppercase">{pl.categoria}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-[12px]">{fmtNum(pl.total_caixas)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-[12px] text-purple-700">{fmtNum(pl.quantidade_expedida)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-[12px] font-bold text-brand-700">{fmtNum(pl.restante)}</td>
                          <td className="px-3 py-1.5 text-[11px] text-ink-500 whitespace-nowrap">
                            {pl.created_at ? new Date(pl.created_at).toLocaleString("pt-BR") : "—"}
                          </td>
                          <td className="px-3 py-1.5 font-mono text-[10px] text-danger whitespace-nowrap">
                            {(pl.invoice_ids_ausentes || []).map((iid) => iid.slice(0, 8)).join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-stone-100 border-t-2 border-ink-900 font-bold text-[12px]">
                      <tr>
                        <td className="px-3 py-2" colSpan={2}>TOTAL ÓRFÃOS</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtNum(estoque.pallets_orfaos.reduce((s, p) => s + (p.total_caixas || 0), 0))}</td>
                        <td className="px-3 py-2 text-right font-mono text-purple-700">{fmtNum(estoque.pallets_orfaos.reduce((s, p) => s + (p.quantidade_expedida || 0), 0))}</td>
                        <td className="px-3 py-2 text-right font-mono text-brand-700">{fmtNum(estoque.pallets_orfaos.reduce((s, p) => s + (p.restante || 0), 0))}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              <div className="text-xs text-ink-600 bg-stone-50 border border-ink-200 rounded-md p-3">
                <strong>Ações possíveis:</strong>
                <ul className="mt-1 space-y-1 list-disc list-inside">
                  <li>Se os pallets são <b>reais</b> (a mercadoria existiu): recadastrar as NFs faltantes com os UUIDs acima</li>
                  <li>Se são <b>duplicatas ou tentativas falhas</b>: entrar em contato com o suporte para rodar o script de correção (remove pallets órfãos + ajusta retiradas)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela entradas */}
      <Card className="overflow-hidden mb-4" data-testid="comparativo-entradas">
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-emerald-900 font-bold uppercase tracking-wider text-xs">
          Entradas (Beneficiadas) — {entradas?.rows?.length || 0} carga(s) · {fmtNum(tE.sacos_total || 0)} sc · Quebra média {fmtNum(tE.quebra_media_pct || 0, 2)}%
        </div>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-ink-200">
            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-500">
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">NF</th>
              <th className="px-3 py-2">Motorista</th>
              <th className="px-3 py-2 text-right">Peso</th>
              {CATS.map((c) => (<th key={c} className="px-3 py-2 text-right">{CAT_LABELS[c]}</th>))}
              <th className="px-3 py-2 text-right">Sacos</th>
              <th className="px-3 py-2 text-right">Quebra %</th>
            </tr>
          </thead>
          <tbody>
            {(entradas?.rows || []).map((r) => (
              <tr key={r.invoice_id} className="border-b border-ink-100 last:border-0">
                <td className="px-3 py-1.5 font-mono text-[12px]">{fmtDateBR(r.data)}</td>
                <td className="px-3 py-1.5 font-mono text-[12px]">#{r.nota_numero || "—"}</td>
                <td className="px-3 py-1.5 text-[12px]">{r.motorista}</td>
                <td className="px-3 py-1.5 text-right font-mono text-[12px]">{fmtNum(r.peso_liquido || 0)} kg</td>
                {CATS.map((c) => (<td key={c} className="px-3 py-1.5 text-right font-mono text-[12px]">{fmtNum(r[c] || 0)}</td>))}
                <td className="px-3 py-1.5 text-right font-mono font-bold text-[12px] text-emerald-800">{fmtNum(r.sacos_total || 0)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-[12px] text-amber-700">{fmtNum(r.quebra_pct || 0, 2)}%</td>
              </tr>
            ))}
            {(entradas?.rows || []).length === 0 && (
              <tr><td colSpan={11} className="px-4 py-4 text-center text-ink-500 text-xs">Nenhuma entrada no período.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Tabela saídas */}
      <Card className="overflow-hidden" data-testid="comparativo-saidas">
        <div className="px-4 py-2 bg-purple-50 border-b border-purple-200 text-purple-900 font-bold uppercase tracking-wider text-xs">
          Saídas (Retiradas) — {saidas?.rows?.length || 0} retirada(s) · {fmtNum(tS.sacos_total || 0)} sc · {fmtNum(tS.total_pallets || 0)} pallets
        </div>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-ink-200">
            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-500">
              <th className="px-3 py-2">Data</th>
              <th className="px-3 py-2">Retirada</th>
              <th className="px-3 py-2">Motorista</th>
              <th className="px-3 py-2">Vendedor</th>
              {CATS.map((c) => (<th key={c} className="px-3 py-2 text-right">{CAT_LABELS[c]}</th>))}
              <th className="px-3 py-2 text-right">Sacos</th>
              <th className="px-3 py-2 text-right">Pallets</th>
            </tr>
          </thead>
          <tbody>
            {(saidas?.rows || []).map((r) => (
              <tr key={r.retirada_id} className="border-b border-ink-100 last:border-0">
                <td className="px-3 py-1.5 font-mono text-[12px]">{fmtDateBR(r.data)}</td>
                <td className="px-3 py-1.5 font-mono text-[12px]">{r.numero || "—"}</td>
                <td className="px-3 py-1.5 text-[12px]">{r.motorista}</td>
                <td className="px-3 py-1.5 text-[12px]">{r.vendedor || "—"}</td>
                {CATS.map((c) => (<td key={c} className="px-3 py-1.5 text-right font-mono text-[12px]">{fmtNum(r[c] || 0)}</td>))}
                <td className="px-3 py-1.5 text-right font-mono font-bold text-[12px] text-purple-800">{fmtNum(r.sacos_total || 0)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-[12px] text-ink-500">{fmtNum(r.total_pallets || 0)}</td>
              </tr>
            ))}
            {(saidas?.rows || []).length === 0 && (
              <tr><td colSpan={11} className="px-4 py-4 text-center text-ink-500 text-xs">Nenhuma saída no período.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Assinaturas impressão */}
      <div className="hidden print:block mt-12 signatures">
        <div className="grid grid-cols-2 gap-16">
          <div className="text-center">
            <div className="border-t border-black pt-2 text-xs">Cebolas Serenata</div>
          </div>
          <div className="text-center">
            <div className="border-t border-black pt-2 text-xs">{cliente}</div>
          </div>
        </div>
      </div>
    </>
  );
}
